import { Cat, KINDS } from '../../../gameplay/registry';
import { C } from '../../../gameplay/types';
import { FX_ONE } from '../fixed';
import { Order, UState, type World } from '../world';
import { orderGather, findNearbyResource, isFarm } from './economy';
import { approach, stopMoving } from './movement';

const BUILD_REACH = FX_ONE / 2 + (FX_ONE >> 2);

/**
 * Check building placement validity at tile (tx,ty).
 * Rejects out of bounds, water, steep slopes, occupied tiles, and placements
 * that would seal off nearby open ground (walls excepted, since sealing is their purpose).
 */
export function canPlace(w: World, kindIdx: number, tx: number, ty: number, owner: number): boolean {
  const b = KINDS[kindIdx].building;
  if (!b) return false;
  const size = b.size;
  if (tx < 1 || ty < 1 || tx + size > w.size - 1 || ty + size > w.size - 1) return false;
  if (KINDS[kindIdx].key === 'dock') return canPlaceDock(w, tx, ty, size, owner);
  const vs = w.size + 1;
  let hmin = Infinity;
  let hmax = -Infinity;
  for (let y = ty; y < ty + size; y++)
    for (let x = tx; x < tx + size; x++) {
      const i = y * w.size + x;
      if (!w.map.passable[i] || w.occupant[i] >= 0) return false;
      const t = w.map.terrain[i];
      if (t === 5 || t === 6) return false;
      // Farms may not overlap other farms.
      let blockedByFarm = false;
      w.forStaticInRadius((x << 10) + 512, (y << 10) + 512, FX_ONE, (o) => {
        if (w.cat[o] === Cat.Building && isFarm(w, o) && x >= w.btx[o] && x < w.btx[o] + 3 && y >= w.bty[o] && y < w.bty[o] + 3) {
          blockedByFarm = true;
          return true;
        }
      });
      if (blockedByFarm) return false;
    }
  for (let y = ty; y <= ty + size; y++)
    for (let x = tx; x <= tx + size; x++) {
      const h = w.map.heights[y * vs + x];
      if (h < hmin) hmin = h;
      if (h > hmax) hmax = h;
    }
  if (hmax - hmin > 48 + size * 6) return false;
  // Must be on explored ground.
  const vis = w.visibility[owner];
  if (vis) for (let y = ty; y < ty + size; y++) for (let x = tx; x < tx + size; x++) if (vis[y * w.size + x] === 0) return false;
  if (b.foodAmount !== undefined || b.classes & (1 << 11)) return true;
  return !sealsGround(w, tx, ty, size);
}

/** Docks straddle the shoreline: some footprint tiles water, some land, none occupied. */
function canPlaceDock(w: World, tx: number, ty: number, size: number, owner: number): boolean {
  let water = 0;
  let land = 0;
  const vis = w.visibility[owner];
  for (let y = ty; y < ty + size; y++)
    for (let x = tx; x < tx + size; x++) {
      const i = y * w.size + x;
      if (w.occupant[i] >= 0) return false;
      if (vis && vis[i] === 0) return false;
      const t = w.map.terrain[i];
      if (t === 5 || t === 6) water++;
      else if (w.map.passable[i]) land++;
      else return false;
    }
  return water >= 3 && land >= 2;
}

/** Detects whether placing a footprint would disconnect the open tiles around it. */
function sealsGround(w: World, tx: number, ty: number, size: number): boolean {
  // Gather ring tiles around the footprint in perimeter order.
  const ring: number[] = [];
  const x0 = tx - 1;
  const y0 = ty - 1;
  const n = size + 2;
  for (let i = 0; i < n; i++) ring.push(y0 * w.size + x0 + i);
  for (let j = 1; j < n; j++) ring.push((y0 + j) * w.size + x0 + n - 1);
  for (let i = n - 2; i >= 0; i--) ring.push((y0 + n - 1) * w.size + x0 + i);
  for (let j = n - 2; j >= 1; j--) ring.push((y0 + j) * w.size + x0);
  const open = ring.map((i) => !(w.map.passable[i] === 0 || w.occupant[i] >= 0));
  // Count open arcs.
  const arcs: number[] = [];
  for (let i = 0; i < ring.length; i++) if (open[i] && !open[(i + ring.length - 1) % ring.length]) arcs.push(ring[i]);
  if (arcs.length <= 1) return false;
  // Bounded BFS from first arc to reach every other arc without crossing the footprint.
  const inFoot = (i: number) => {
    const x = i % w.size;
    const y = (i / w.size) | 0;
    return x >= tx && x < tx + size && y >= ty && y < ty + size;
  };
  const seen = new Set<number>([arcs[0]]);
  const q = [arcs[0]];
  const targets = new Set(arcs.slice(1));
  let head = 0;
  while (head < q.length && seen.size < 2500 && targets.size > 0) {
    const c = q[head++];
    const cx = c % w.size;
    const cy = (c / w.size) | 0;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= w.size || ny >= w.size) continue;
      const ni = ny * w.size + nx;
      if (seen.has(ni) || inFoot(ni) || w.map.passable[ni] === 0 || w.occupant[ni] >= 0) continue;
      seen.add(ni);
      targets.delete(ni);
      q.push(ni);
    }
  }
  // If the search ran out of budget, the region is large and treated as connected.
  return targets.size > 0 && seen.size < 2500;
}

export function orderBuild(w: World, s: number, b: number): void {
  w.order[s] = Order.Build;
  w.target[s] = w.idOf(b);
  w.state[s] = UState.Walking;
  approach(w, s, b);
}

function nextBuildOrIdle(w: World, s: number, finished: number): void {
  // Engineers only repair: when done they stand by rather than go building or gathering.
  if (KINDS[w.kind[s]].unit!.classes & C.Engineer) {
    w.order[s] = Order.None;
    w.state[s] = UState.Idle;
    stopMoving(w, s);
    return;
  }
  const q = w.buildQueue[s];
  while (q && q.length > 0) {
    const nb = w.resolve(q.shift()!);
    if (nb >= 0 && !w.built[nb]) {
      orderBuild(w, s, nb);
      return;
    }
  }
  w.buildQueue[s] = null;
  // Return to any other unfinished foundation nearby (e.g. a row of houses placed one after another).
  const pending = nearestFoundation(w, s, finished);
  if (pending >= 0) {
    orderBuild(w, s, pending);
    return;
  }
  // Auto-work: farm builders farm; drop-off builders gather nearby matching resource.
  if (finished >= 0) {
    const bdef = KINDS[w.kind[finished]].building!;
    if (bdef.foodAmount !== undefined) {
      orderGather(w, s, finished);
      return;
    }
    if (bdef.dropoff && bdef.dropoff.length < 4) {
      const t = findNearbyResource(w, s, bdef.dropoff[0], w.x[finished], w.y[finished], -1);
      if (t >= 0) {
        orderGather(w, s, t);
        return;
      }
    }
  }
  w.order[s] = Order.None;
  w.state[s] = UState.Idle;
  stopMoving(w, s);
}

/** Nearest unfinished non-farm foundation of the villager's owner within 16 tiles. */
function nearestFoundation(w: World, s: number, exclude: number): number {
  let best = -1;
  let bestD = Infinity;
  const owner = w.owner[s];
  w.forStaticInRadius(w.x[s], w.y[s], 16 * FX_ONE, (o, d2) => {
    if (o === exclude || w.cat[o] !== Cat.Building || w.owner[o] !== owner || w.built[o]) return;
    if (KINDS[w.kind[o]].building!.foodAmount !== undefined) return;
    if (w.isUnreachable(owner, w.idOf(o))) return;
    if (d2 < bestD || (d2 === bestD && o < best)) {
      bestD = d2;
      best = o;
    }
  });
  return best;
}

export function updateConstruction(w: World): void {
  const n = w.highSlot;
  for (let s = 0; s < n; s++) {
    if (w.alive[s] && w.cat[s] === Cat.Building) {
      w.builders[s] = w.buildersNext[s];
      w.buildersNext[s] = 0;
    }
  }
  for (let s = 0; s < n; s++) {
    if (!w.alive[s] || w.cat[s] !== Cat.Unit) continue;
    const ord = w.order[s];
    if (ord !== Order.Build && ord !== Order.Repair) continue;
    const b = w.resolve(w.target[s]);
    if (b < 0 || w.cat[b] !== Cat.Building || w.owner[b] !== w.owner[s]) {
      nextBuildOrIdle(w, s, -1);
      continue;
    }
    if (ord === Order.Build && w.built[b]) {
      nextBuildOrIdle(w, s, b);
      continue;
    }
    if (ord === Order.Repair && (!w.built[b] || w.hp[b] >= w.maxHp[b])) {
      if (!w.built[b]) w.order[s] = Order.Build;
      else nextBuildOrIdle(w, s, -1);
      continue;
    }
    if (w.isUnreachable(w.owner[s], w.target[s])) {
      nextBuildOrIdle(w, s, -1);
      continue;
    }
    if (w.edgeDist(b, w.x[s], w.y[s]) > BUILD_REACH) {
      if (!w.moving[s] && !w.paths[s]) approach(w, s, b);
      w.state[s] = UState.Walking;
      continue;
    }
    if (w.moving[s]) stopMoving(w, s);
    w.timer[s] = 0;
    w.state[s] = UState.Building;
    const dx = w.x[b] - w.x[s];
    const dy = w.y[b] - w.y[s];
    const dd = Math.max(1, Math.floor(Math.sqrt(dx * dx + dy * dy)));
    w.fdx[s] = Math.trunc((dx * 1000) / dd);
    w.fdy[s] = Math.trunc((dy * 1000) / dd);
    w.buildersNext[b]++;
    const nb = Math.max(1, w.builders[b]);
    const p = w.players[w.owner[b]];
    const total = p.stats[w.kind[b]].buildTicks * 1000;
    if (ord === Order.Build) {
      // Diminishing returns: n builders finish in 3T/(n+2).
      const add = Math.trunc(3000 / (nb + 2));
      const before = w.buildProg[b];
      w.buildProg[b] = Math.min(total, before + add);
      const hpGain = Math.trunc((w.maxHp[b] * w.buildProg[b]) / total) - Math.trunc((w.maxHp[b] * before) / total);
      w.hp[b] = Math.min(w.maxHp[b], w.hp[b] + hpGain);
      if (w.buildProg[b] >= total) {
        w.built[b] = 1;
        w.onBuildingComplete(b);
        w.events.push({ e: 'built', id: w.idOf(b), kind: w.kind[b], owner: w.owner[b] });
      }
    } else {
      // Repair at half construction speed (engineers three times that); a full repair costs half the
      // building's price, paid as it goes.
      const fast = KINDS[w.kind[s]].unit!.classes & C.Engineer ? 3 : 1;
      const rate = Math.max(fast, Math.trunc((w.maxHp[b] * 1500 * fast) / (nb + 2) / total));
      const gain = Math.min(rate, w.maxHp[b] - w.hp[b]);
      const cost = p.stats[w.kind[b]].cost;
      const step = [0, 0, 0, 0];
      let ok = true;
      for (let r = 0; r < 4; r++) {
        step[r] = Math.trunc((cost[r] * FX_ONE * gain + 2 * w.maxHp[b] - 1) / (2 * w.maxHp[b]));
        if (p.res[r] < step[r]) ok = false;
      }
      if (!ok) {
        w.events.push({ e: 'notice', owner: w.owner[b], text: 'Not enough resources to repair' });
        nextBuildOrIdle(w, s, -1);
        continue;
      }
      for (let r = 0; r < 4; r++) p.res[r] -= step[r];
      w.hp[b] += gain;
    }
  }
}

/** Resources (whole units) to fully repair building slot b: half its price, scaled by missing hit points. */
export function repairCost(w: World, b: number): number[] {
  const cost = w.players[w.owner[b]].stats[w.kind[b]].cost;
  const missing = Math.max(0, w.maxHp[b] - w.hp[b]);
  return cost.map((c) => Math.ceil((c * missing) / (2 * Math.max(1, w.maxHp[b]))));
}
